import { useMeQuery } from '@/generated/graphql'
import { useQueryClient } from '@tanstack/react-query'
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from 'aws-amplify/auth'
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { AuthContext } from './context'

const ME_STALE_TIME_MS = 15 * 60 * 1000
const ME_QUERY_KEY = ['me'] as const

function getUsernameFromAccessToken(payload: object): string | undefined {
  if (!('username' in payload)) return undefined
  return typeof payload.username === 'string' ? payload.username : undefined
}

export default function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient()
  const [userId, setUserId] = useState<string>(undefined)
  const [isInitializing, setIsInitializing] = useState<boolean>(true)

  const initialize = useCallback(async () => {
    try {
      const { tokens } = await fetchAuthSession()
      if (!tokens?.accessToken) return

      const username = getUsernameFromAccessToken(tokens.accessToken.payload)
      if (username) {
        setUserId(username)
        return
      }

      const currentUser = await getCurrentUser()
      setUserId(currentUser.username)
    } catch (error) {
      console.error(error)
    } finally {
      setIsInitializing(false)
    }
  }, [])

  useEffect(() => {
    void initialize()
  }, [initialize])

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      const { nextStep } = await signIn({
        username: email,
        password,
      })

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED')
        throw new Error('NEW_PASSWORD_REQUIRED')
      if (nextStep.signInStep !== 'DONE')
        throw new Error(
          `Unable to login, because challenge '${nextStep.signInStep}' is mandated and the case is not handled.`
        )

      const { username } = await getCurrentUser()
      setUserId(username)
    },
    []
  )

  const loginWithCode = useCallback(async (email: string): Promise<void> => {
    let nextStep: string

    nextStep = (
      await signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP',
        },
      })
    ).nextStep.signInStep

    if (nextStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
      nextStep = (
        await confirmSignIn({
          challengeResponse: 'EMAIL_OTP',
        })
      ).nextStep.signInStep
    }

    if (nextStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE')
      throw new Error(
        `Unable to login, because challenge '${nextStep}' is mandated and the case is not handled.`
      )
  }, [])

  const confirmCode = useCallback(async (code: string): Promise<void> => {
    const { nextStep } = await confirmSignIn({ challengeResponse: code })

    if (nextStep.signInStep !== 'DONE')
      throw new Error(
        `Unable to confirm sign in, because challenge '${nextStep.signInStep}' is mandated and the case is not handled.`
      )

    const { username } = await getCurrentUser()
    setUserId(username)
  }, [])

  const confirmNewPassword = useCallback(
    async (newPassword: string): Promise<void> => {
      const { nextStep } = await confirmSignIn({
        challengeResponse: newPassword,
      })

      if (nextStep.signInStep !== 'DONE')
        throw new Error(
          `Unable to confirm sign in, because challenge '${nextStep.signInStep}' is mandated and the case is not handled.`
        )

      const { username } = await getCurrentUser()
      setUserId(username)
    },
    []
  )

  const logout = useCallback(async (): Promise<void> => {
    await queryClient.removeQueries({ queryKey: ME_QUERY_KEY })
    await signOut()
    setUserId(undefined)
  }, [queryClient])

  const { data, isLoading } = useMeQuery(
    {},
    {
      enabled: !!userId,
      staleTime: ME_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  )
  const user = useMemo(() => data?.me || undefined, [data])

  const value = useMemo(
    () => ({
      isInitializing,
      userId,
      isAuthenticated: !!userId,
      isLoading,
      user,
      login,
      loginWithCode,
      confirmNewPassword,
      confirmCode,
      logout,
    }),
    [
      isInitializing,
      userId,
      isLoading,
      user,
      login,
      loginWithCode,
      confirmNewPassword,
      confirmCode,
      logout,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
